/* eslint-disable max-len */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import Tips from '../common/Tips'
import { SimpleTeam } from '../../../states/types/team'

interface Props {
  memberIdList: number[]
  callbackMembers: (teams: { id: number, name: string }[]) => void
  memberList: { id: number, name: string, profile_image: string | null, active: boolean }[]
  teamList: SimpleTeam[]
  isClear?: boolean
}

const AddMemberGroupingModule: FC<Props> = (props) => {
  const {
    memberIdList,
    callbackMembers,
    memberList,
    teamList,
    isClear,
  } = props

  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [selectMembers, setSelectMembers] = useState<{id: number, name: string}[]>([])

  const [members, setMembers] = useState<{id: number, name: string, profile_image: string | null, active: boolean}[]>([])
  const [searchWord, setSearchWord] = useState<string>("")

  const membersInit = useMemo(() => (
    memberList || []
  ), [memberList])

  const notTeamMembers = useMemo(() => {
    const teamMemberIds = Array.from(new Set(teamList.map((v) => [...v.managers.map((w) => w.id), ...v.members.map((w) => w.id)]).flat()))
    return memberList.filter((v) => !teamMemberIds.includes(v.id))
  }, [teamList, memberList])

  const getUniqueValues = ([...array]) => (
    array.filter((value, _index, self) => self.indexOf(value) === self.lastIndexOf(value))
  )

  const clickMember = (memberId: number) => {
    if (selectMembers.map((v) => v.id).includes(memberId)) {
      setSelectMembers(selectMembers.filter((v) => v.id !== memberId))
    } else {
      setSelectMembers([...selectMembers, { id: memberId, name: members.find((v) => v.id === memberId)?.name || "" }])
    }
  }

  const clickMembers = (memberIds: number[]) => {
    const tmpSelectMembers = memberList.filter((v) => memberIds.includes(v.id)).map((v) => ({ id: v.id, name: v.name }))
    setSelectMembers(Array.from(new Map([...selectMembers, ...tmpSelectMembers].map((member) => [member.id, member])).values()))
  }

  const compile = (conditions: string[]) => {
    const joinAnd = (arr: string[]) => `^(?=[\\s\\S]*${arr.join(')(?=[\\s\\S]*')})`
    const rx = joinAnd(conditions).replace(/=\[\\s\\S\]\*-/g, '![\\s\\S]*')
    return new RegExp(rx)
  }

  const searchTeam = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setMembers([...membersInit])
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const list = membersInit.filter((member) => {
      if (member === null) return false
      return (compile(word).test(member.name.toLowerCase())) || selectMembers.map((v) => v.id)?.includes(member.id)
    })
    setMembers(sortBy(list, 'id'))
  }

  const resetTeam = () => {
    setSearchWord("")
    setMembers([...membersInit])
  }

  const openSearch = () => {
    setIsSearchDisplay(true)
  }

  const closeSearch = () => {
    setIsSearchDisplay(false)
    resetTeam()
    if (!getUniqueValues([...selectMembers.map((v) => v.id), ...memberIdList]).length) return
    callbackMembers(selectMembers)
  }

  useEffect(() => {
    setMembers(memberList || [])
  }, [memberList])

  useEffect(() => {
    if (!memberList) return
    setSelectMembers(memberList.filter((v) => memberIdList?.includes(v.id)))
  }, [memberIdList, memberList])

  useEffect(() => {
    if (!isClear) return
    setSelectMembers([])
  }, [isClear])

  return (
    <>
      <div
        className="add_button"
        onClick={() => openSearch()}
        role="none"
      >
        <FontAwesomeIcon icon={['fas', 'plus']} />
        &nbsp;
        追加
        <div className={`search_box ${isSearchDisplay || 'none'}`} style={{ left: "-250px" }}>
          <div className="search_detail" style={{ width: "500px" }}>
            <div className="category">
              <div className="category_title">
                メンバー
                {
                  selectMembers.length ? (
                    <div
                      className="select_reset"
                      role="none"
                      onClick={() => setSelectMembers([])}
                    >
                      リセット
                    </div>
                  ) : (
                    <></>
                  )
                }
              </div>
              <div className="search_area" style={{ marginBottom: "16px" }}>
                <FontAwesomeIcon
                  icon={['fas', 'search']}
                  className="icon"
                />
                <input
                  type="text"
                  placeholder="チーム名を検索"
                  onChange={(event) => searchTeam(event.target.value)}
                  value={searchWord}
                />
                {
                  searchWord && (
                    <button type="button" className="reset" onClick={resetTeam}>
                      <FontAwesomeIcon icon={['far', 'times-circle']} />
                    </button>
                  )
                }
              </div>
              {
                !!notTeamMembers.length && !searchWord && (
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      position: "relative",
                      padding: "16px 8px 8px",
                      marginBottom: "16px",
                      display: "flex",
                      flexFlow: "column",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "-6px",
                        backgroundColor: "#FFFFFF",
                        padding: "0 4px",
                        fontWeight: "bold",
                      }}
                    >
                      チームに属さないメンバー
                    </div>
                    <div>
                      {
                        notTeamMembers.map((member) => (
                          <button
                            type="button"
                            className={`chip ${selectMembers.map((v) => v.id).includes(member.id) ? "checked" : ""}`}
                            key={`member_${member.id}`}
                            onClick={() => clickMember(member.id)}
                          >
                            {
                              member.profile_image ? (
                                <img
                                  alt="profile"
                                  src={member.profile_image}
                                  className="profile_icon"
                                  style={{
                                    borderRadius: "50%",
                                    width: "20px",
                                  }}
                                />
                              ) : (
                                <img
                                  alt=""
                                  src={`/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                  className="profile_icon"
                                  style={{
                                    borderRadius: "50%",
                                    width: "20px",
                                  }}
                                />
                              )
                            }
                            {member.name}
                            {
                              member.active || (
                                <Tips
                                  description="休眠中💤"
                                  className=""
                                  icon={false}
                                >
                                  <>
                                    💤
                                  </>
                                </Tips>
                              )
                            }
                          </button>
                        ))
                      }
                    </div>
                  </div>
                )
              }
              {
                searchWord && !teamList.filter((v) => v.managers.length || v.members.length).filter((v) => v.name.indexOf(searchWord) >= 0).length && (
                  <>
                    {`「${searchWord}」に該当するチーム名がありませんでした`}
                  </>
                )
              }
              {
                teamList.filter((v) => v.managers.length || v.members.length).filter((v) => !searchWord || v.name.indexOf(searchWord) >= 0).map((team) => (
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      position: "relative",
                      padding: "24px 8px 8px",
                      marginBottom: "16px",
                      display: "flex",
                      flexFlow: "column",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "-6px",
                        backgroundColor: "#FFFFFF",
                        padding: "0 4px",
                        display: "flex",
                        alignItems: "baseline",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                        }}
                      >
                        チーム：
                        {team.name}
                      </div>
                      <div
                        onClick={() => clickMembers(Array.from(new Set([...team.managers.map((v) => v.id), ...team.members.map((v) => v.id)])))}
                        role="none"
                        style={{
                          fontSize: "10px",
                          cursor: "pointer",
                          color: "#4572BA",
                        }}
                      >
                        全選択
                      </div>
                    </div>
                    {
                      !!team.managers.length && (
                        <div
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            position: "relative",
                            padding: "8px",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: "-6px",
                              backgroundColor: "#FFFFFF",
                              padding: "0 4px",
                              display: "flex",
                              alignItems: "baseline",
                              gap: "8px",
                            }}
                          >
                            マネージャー
                            <div
                              onClick={() => clickMembers(Array.from(new Set([...team.managers.map((v) => v.id)])))}
                              role="none"
                              style={{
                                fontSize: "10px",
                                cursor: "pointer",
                                color: "#4572BA",
                              }}
                            >
                              全選択
                            </div>
                          </div>
                          {
                            team.managers.map((member) => (
                              <button
                                type="button"
                                className={`chip ${selectMembers.map((v) => v.id).includes(member.id) ? "checked" : ""}`}
                                key={`member_${member.id}`}
                                onClick={() => clickMember(member.id)}
                              >
                                {
                                  member.profile_image ? (
                                    <img
                                      alt="profile"
                                      src={member.profile_image}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "20px",
                                      }}
                                    />
                                  ) : (
                                    <img
                                      alt=""
                                      src={`/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "20px",
                                      }}
                                    />
                                  )
                                }
                                {member.name}
                                {
                                  member.active || (
                                    <Tips
                                      description="休眠中💤"
                                      className=""
                                      icon={false}
                                    >
                                      <>
                                        💤
                                      </>
                                    </Tips>
                                  )
                                }
                              </button>
                            ))
                          }
                        </div>
                      )
                    }
                    {
                      !!team.members.length && (
                        <div
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            position: "relative",
                            padding: "8px",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: "-6px",
                              backgroundColor: "#FFFFFF",
                              padding: "0 4px",
                              display: "flex",
                              alignItems: "baseline",
                              gap: "8px",
                            }}
                          >
                            メンバー
                            <div
                              onClick={() => clickMembers(Array.from(new Set([...team.members.map((v) => v.id)])))}
                              role="none"
                              style={{
                                fontSize: "10px",
                                cursor: "pointer",
                                color: "#4572BA",
                              }}
                            >
                              全選択
                            </div>
                          </div>
                          {
                            team.members.map((member) => (
                              <button
                                type="button"
                                className={`chip ${selectMembers.map((v) => v.id).includes(member.id) ? "checked" : ""}`}
                                key={`member_${member.id}`}
                                onClick={() => clickMember(member.id)}
                              >
                                {
                                  member.profile_image ? (
                                    <img
                                      alt="profile"
                                      src={member.profile_image}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "20px",
                                      }}
                                    />
                                  ) : (
                                    <img
                                      alt=""
                                      src={`/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "20px",
                                      }}
                                    />
                                  )
                                }
                                {member.name}
                                {
                                  member.active || (
                                    <Tips
                                      description="休眠中💤"
                                      className=""
                                      icon={false}
                                    >
                                      <>
                                        💤
                                      </>
                                    </Tips>
                                  )
                                }
                              </button>
                            ))
                          }
                        </div>
                      )
                    }
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
      <div className={`close_screen ${isSearchDisplay || 'none'}`} role="presentation" onClick={closeSearch} />
    </>
  )
}

AddMemberGroupingModule.defaultProps = {
  isClear: false,
}

export default AddMemberGroupingModule
